/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationGlueDefaultNamespace,
  GamoraFoundationNodeArgs,
  GamoraUUIDGuard,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakMessageAppPresentationCompletedGuard,
  HoruzProtocolChulakMessageAppPresentationCompletedGuardInput,
} from '../../guards/message/app-presentation-completed.js';
import {
  HoruzProtocolChulakServiceStateYielder,
  HoruzProtocolChulakServiceStateYielderCompletedAppPresentationInput,
} from './enums.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakLastState } from './../../states.js';
import { HoruzProtocolChulakMessageAppPresentationEnterGuard } from '../../guards/message/app-presentation-enter.js';
import { HoruzProtocolChulakMessageAppPresentationExitGuard } from '../../guards/message/app-presentation-exit.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakServiceStateBase } from './base.js';
import { HoruzProtocolChulakServiceStateContext } from '../context.js';
import { HoruzProtocolChulakServiceStateMap } from '../../statemap.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:service:app-presentation',
});

/**
 * Server
 *
 * AppPresentation state
 * @public
 */
export class HoruzProtocolChulakServiceStateAppPresentation extends HoruzProtocolChulakServiceStateBase<
  typeof HoruzProtocolChulakServiceStateMap,
  HoruzProtocolChulakServiceStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
    this._initialize();
  }

  /**
   * Initializer
   */
  private _initialize() {
    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistAppPresentationMessage,
      this._onPersistAppPresentationMessageYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.BroadcastAppPresentationToClients,
      this._onBroadcastAppPresentationToClientsYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistAppPresentationRecoveryFlag,
      this._onPersistAppPresentationRecoveryFlagYielder.bind(this),
    );
  }

  /**
   * Event
   *
   * Persists app presentation message to storage (single pod, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistAppPresentationMessageYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
    input: HoruzProtocolChulakServiceStateYielderCompletedAppPresentationInput,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        // Update service session
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          // Get service session
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Get start message
          const presentationCompleted =
            HoruzProtocolChulakMessageAppPresentationCompletedGuard.fromJSON({
              compact: input.compact,
            } as HoruzProtocolChulakMessageAppPresentationCompletedGuardInput);

          // Set data to service session
          serviceSession.setData(
            HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED,
            HoruzProtocolChulakMessageModule.Encode(presentationCompleted),
          );

          // Mark yielder as yielded
          serviceSession.addYielded(
            HoruzProtocolChulakServiceStateYielder.PersistAppPresentationMessage,
          );

          // Store service session
          await this.serviceSessionStore.upsert(serviceSession);
        }
      },
    );
  };

  /**
   * Event
   *
   * Marks recovery flag for reconnecting clients (single pod via sync-session queue, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistAppPresentationRecoveryFlagYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Mark as yielded only if not already marked
          if (
            !serviceSession.hasYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastAppPresentationToClients,
            )
          ) {
            serviceSession.addYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastAppPresentationToClients,
            );
            await this.serviceSessionStore.upsert(serviceSession);
          }
        }
      },
    );
  };

  /**
   * Event
   *
   * Broadcasts app presentation to local WebSocket clients (all pods via notify-session SNS, no lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onBroadcastAppPresentationToClientsYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    const hasServiceSession =
      await this.serviceSessionStore.has(serviceSessionUuid);

    // No session, nothing to do
    if (!hasServiceSession) {
      return;
    }

    // Read session data WITHOUT lock (read-only operation)
    const serviceSession =
      await this.serviceSessionStore.get(serviceSessionUuid);

    // Get the presentationCompleted message to send to clients
    const presentationCompleted = serviceSession.getDataByType(
      HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED,
    );

    // Check if THIS pod has any active WebSocket contexts for this session
    const myActiveContextIdentifiers: string[] = [];
    for (const serviceContextIdentifier of serviceSession.contextIdentifiers) {
      if (this.$controller.$hasContext(serviceContextIdentifier)) {
        myActiveContextIdentifiers.push(serviceContextIdentifier);
      }
    }

    // Send messages to OUR active WebSocket contexts (no lock needed)
    // Only pods with local contexts will send
    if (myActiveContextIdentifiers.length > 0) {
      for (const serviceContextIdentifier of myActiveContextIdentifiers) {
        const controllerContext = this.$controller.$getContext(
          serviceContextIdentifier,
        );
        const serviceStateContext = controllerContext.stateContext;

        this._trySendMessageAndTransition(
          serviceStateContext,
          'Done',
          presentationCompleted,
        );
      }
    }
  };

  /**
   * Enter state
   */
  override async $enter(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );
            // Get session
            const serviceSession = await this.serviceSessionStore.get(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Get start message
            const startEnter =
              HoruzProtocolChulakMessageAppPresentationEnterGuard.fromJSON({});

            // Send start message
            await this.send(serviceStateContext, startEnter);

            // Store
            serviceSession.lastState =
              HoruzProtocolChulakLastState.AppPresentation;
            await this.serviceSessionStore.upsert(serviceSession);

            // Recovery
            if (
              serviceSession.hasYielded(
                HoruzProtocolChulakServiceStateYielder.BroadcastAppPresentationToClients,
              )
            ) {
              const presentationCompleted = serviceSession.getDataByType(
                HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED,
              );

              this._trySendMessageAndTransition(
                serviceStateContext,
                'Done',
                presentationCompleted,
              );
            }
          } catch (e: any) {
            debug.log('($enter): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($enter): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      // Aborting or stopping are the next states
      const contextState = this.$controller.$getContext(
        serviceStateContext.contextIdentifier,
      );
      if (
        contextState.stateNext == 'Abort' ||
        contextState.stateNext == 'Stopped'
      ) {
        return;
      }

      // Finalize
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );
            // Get session
            const serviceSession = await this.serviceSessionStore.get(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Get start message
            const startExit =
              HoruzProtocolChulakMessageAppPresentationExitGuard.fromJSON({});

            // Send message
            await this.send(serviceStateContext, startExit);

            // Store
            await this.serviceSessionStore.upsert(serviceSession);
          } catch (e: any) {
            debug.log('($exit): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($exit): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Register yielders
   * @public
   */
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistAppPresentationMessage,
    input: HoruzProtocolChulakServiceStateYielderCompletedAppPresentationInput,
  ): Promise<void>;
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.BroadcastAppPresentationToClients,
  ): Promise<void>;
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistAppPresentationRecoveryFlag,
  ): Promise<void>;
  yielder<K extends keyof typeof HoruzProtocolChulakServiceStateYielder>(
    sessionUuid: GamoraUUIDGuard,
    name: K,
    ...args: unknown[]
  ): Promise<void> {
    return super.$executeYielder(
      GamoraFoundationGlueDefaultNamespace,
      name,
      sessionUuid,
      ...args,
    );
  }
}
